import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box, Card, CardBody, VStack, Heading, Text, Button } from '@chakra-ui/react';
import { useZaraEnabled } from './zara/config';
import { ZaraPreviewSync } from './zara/ZaraPreviewSync';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { BrandProvider } from './brand/BrandContext';
import { AgentContextProvider } from './agent';
import { PipelineShell } from './shell/PipelineShell';
import { LandingPage } from './pages/Landing';
import { OnboardingPage } from './pages/Onboarding';
import { WorkspacePage } from './pages/Onboarding/WorkspacePage';
import { BrandPage as OnboardingBrandPage } from './pages/Onboarding/BrandPage';
import { ConnectPage } from './pages/Onboarding/ConnectPage';
import { BrandPage } from './pages/Brand';
import { PlanPerformancePage } from './pages/PlanPerformance';
import { PlanDetailPage } from './pages/PlanDetail';
import { PlanOnboardingPage } from './pages/Onboarding/PlanOnboardingPage';
import { ImageStudioPage } from './pages/ImageStudio';
import { IntegrationsPage } from './pages/Integrations';
import { HomePage } from './pages/Home';
import { UploadPage } from './pages/Upload';
import { DetectReviewPage } from './pages/DetectReview';
import { CampaignsPage } from './pages/Campaigns';
import { CampaignDetailPage } from './pages/CampaignDetail';
import { AdsPage } from './pages/Ads';
import { ProductAdsPage } from './pages/ProductAds';
import { GenerateAdsWizard } from './pages/GenerateAds';
import { UgcAdsWizard } from './pages/UgcWizard';
import { UgcAdsPage } from './pages/UgcAds';
import { MediaLibraryPage } from './pages/MediaLibrary';
import { CatalogBrowserPage } from './pages/CatalogBrowser';
import { CatalogV2Page } from './pages/CatalogV2';
import { SettingsPage } from './pages/Settings';
import { TeamPage } from './pages/Team';
import { InvitePage } from './pages/Invite';
import { SalesDemosPage } from './pages/SalesDemos';
import { AdminSettingsPage } from './pages/AdminSettings';
import { AdminShopifyRequestsPage } from './pages/AdminShopifyRequests';
import { AdminShopifyHealthPage } from './pages/AdminShopifyHealth';
import { AdminSystemConfigPage } from './pages/AdminSystemConfig';
import { SettingsV2Page } from './pages/SettingsV2';
import { BrandV2Page } from './pages/BrandV2';
import { RenderActivityPage } from './pages/RenderActivity';
import { AdShowcasePage } from './pages/AdShowcase';
import { AdShowcasePublicPage } from './pages/AdShowcasePublic';
// M8.1 Connections merge — flag-gated OFF. The page import is static;
// the inline flag below is what gates registration. Section routes
// live inside ConnectionsV2Page so this file's unprefixed paths stay
// the pre-merge table.
import { ConnectionsV2Page } from './pages/ConnectionsV2';
// M8.1 Agent home at /v2/home — flag-gated OFF. Static import; the
// inline flag below gates route registration. Legacy /home stays
// mounted (coexistence).
import { HomeV2Page } from './pages/HomeV2';
// M8.1 Plans V2 — flag-gated OFF. One static import. Child paths
// (list, detail, thread, draft, performance) are registered inside
// PlansV2Page, same as /v2/connections/*. Flag off: this Route is
// not emitted and /v2/plans/* falls through to RootRedirect.
// Legacy /plans/:planId stays mounted either way.
import { PlansV2Page } from './pages/PlansV2';
// M8.2 Runs observational surface — flag-gated OFF. One splat route.
// Child paths live inside RunsV2Page, same pattern as Connections.
import { RunsV2Page } from './pages/RunsV2';
// M8.2 Decision Inbox — flag-gated OFF. One splat route. List and
// detail are children inside DecisionsV2Page. The inline strict
// `=== 'true'` check below is what registers the route.
import { DecisionsV2Page } from './pages/DecisionsV2';
// M8.3 Finance — flag-gated OFF. One splat. Child paths live inside
// FinanceV2Page. The route gate keeps the literal env comparison so
// a flag-off build drops the route, and also calls isFinanceV2Enabled()
// which is that same comparison.
import { FinanceV2Page } from './pages/FinanceV2';
import { isFinanceV2Enabled } from './pages/FinanceV2/flag';
// M8 item 5 — standalone /v2/brand/rights, independent of
// VITE_BRAND_V2_ENABLED. One Route behind VITE_RIGHTS_V2_ENABLED.
// Registered BEFORE the /v2/brand/* splat so React Router matches
// the more specific path first.
import { RightsV2Page } from './pages/RightsV2';
// M8 item 6 — standalone /v2/runs/activations, independent of
// VITE_RUNS_V2_ENABLED. One Route behind VITE_ACTIVATIONS_V2_ENABLED.
// Registered BEFORE /v2/runs/* so React Router matches the more
// specific path first.
import { ActivationsV2Page } from './pages/ActivationsV2';
// M8 item 7 — nested /v2/settings/audit, independent of
// VITE_SETTINGS_V2_ENABLED. One Route behind VITE_AUDIT_V2_ENABLED.
// Registered BEFORE /v2/settings so React Router matches the more
// specific path first.
import { AuditV2Page } from './pages/AuditV2';
// M8.4 Creative Library — flag-gated OFF. One splat at
// /v2/catalog/library/*. The list and the detail view are children
// inside CreativeLibraryPage. The inline strict `=== 'true'` check
// below is what registers the route.
import { CreativeLibraryPage } from './pages/CreativeLibrary';

const ZaraApp = lazy(() => import('./zara/ZaraApp'));

// Flag-off guarantee: Vite inlines this comparison. When
// VITE_PERFORMANCE_V2_ENABLED is not exactly 'true', the branch below
// is dropped, so this page is neither in the main chunk nor emitted as
// its own chunk, and /v2/performance is not registered. Hitting that
// path is not an HTTP 404 — path="*" sends it through RootRedirect to
// /home (signed in) or /landing (signed out), the same fallback as any
// other unknown path. The build is not byte-identical to one from
// before this route: getPlanPerformance still understands an optional
// live block, and the phase-1 page ignores that field.
const performanceV2On = import.meta.env.VITE_PERFORMANCE_V2_ENABLED === 'true';
const PerformanceV2Page = performanceV2On
  ? lazy(() => import('./pages/PerformanceV2'))
  : null;

// Reorg: primary nav is Brand / Campaigns / Ads. Upload + Detect retired
// from the sidebar but their routes remain so deep links keep working
// (they fold into the Generate Ads wizard's empty-state upload step).

export function App() {
  return (
    <AuthProvider>
      <BrandProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </BrandProvider>
    </AuthProvider>
  );
}

// Route tree App renders inside BrowserRouter. Tests mount this with
// MemoryRouter so flag on/off hits the same registration as production.
export function AppRoutes() {
  return (
    <>
      <ZaraPreviewSync />
      <AgentContextProvider>
          <Routes>
            <Route element={<PipelineShell />}>
              <Route path="/home"           element={<RequireAuth><HomePage /></RequireAuth>} />
              <Route path="/zara/*"         element={<RequireAuth><ZaraRoute /></RequireAuth>} />
              <Route path="/brand"          element={<RequireAuth><BrandPage /></RequireAuth>} />
              <Route path="/plans/:planId/performance" element={<RequireAuth><PlanPerformancePage /></RequireAuth>} />
              {performanceV2On && PerformanceV2Page && (
                <Route
                  path="/v2/performance"
                  element={(
                    <RequireAuth>
                      <Suspense fallback={<Text color="brand.muted" fontSize="sm">Loading performance…</Text>}>
                        <PerformanceV2Page />
                      </Suspense>
                    </RequireAuth>
                  )}
                />
              )}
              <Route path="/plans/:planId" element={<RequireAuth><PlanDetailPage /></RequireAuth>} />
              <Route path="/integrations"   element={<RequireAuth><IntegrationsPage /></RequireAuth>} />
              <Route path="/image-studio"   element={<RequireAuth><ImageStudioPage /></RequireAuth>} />
              {/* Legacy redirect — /image-gen was the wizard page retired
                  2026-10-14 (owner directive option 1). Bookmarks and
                  external links bounce to the new chat-driven studio. */}
              <Route path="/image-gen"      element={<Navigate to="/image-studio" replace />} />
              <Route path="/campaigns"      element={<RequireAuth><CampaignsPage /></RequireAuth>} />
              <Route path="/campaigns/:id"  element={<RequireAuth><CampaignDetailPage /></RequireAuth>} />
              <Route path="/product-ads"    element={<RequireAuth><ProductAdsPage /></RequireAuth>} />
              {/* /ads is now a secondary route — primary nav points at
                  /product-ads. /ads is still resolvable for the post-
                  generate redirect (?campaignRunId=X) which shows the
                  run-status poller + flat ad gallery. */}
              <Route path="/ads"            element={<RequireAuth><AdsPage /></RequireAuth>} />
              {/* Operator board for live asset pipeline state (stalls,
                  stages, diagnostics). Secondary nav only. */}
              <Route path="/render-activity" element={<RequireAuth><RenderActivityPage /></RequireAuth>} />
              <Route path="/generate-ads"   element={<RequireAuth><GenerateAdsWizard /></RequireAuth>} />
              {/* UGC-ads wizard (Phase 2). Entry via ?ugc=<mediaId>
                  deep-links from the Media Library and the (future,
                  Phase 4) UGC Ads page. Walks the operator from a
                  UGC post to a product/category/brand/promotional
                  attachment and ad generation against a matching
                  campaign. Not in primary nav — reached from those
                  surfaces or directly. */}
              <Route path="/ugc-wizard"     element={<RequireAuth><UgcAdsWizard /></RequireAuth>} />
              {/* UGC Ads (Phase 4). Primary-nav peer of Product Ads —
                  filterable list of UGC media with attachments + ads
                  per row. ?mediaId=<id> deep-links to a specific row. */}
              <Route path="/ugc-ads"        element={<RequireAuth><UgcAdsPage /></RequireAuth>} />
              {/* Detect Review — secondary nav entry. Operator queue
                  for reviewing AI-detected products before they land in
                  the Product Catalog. */}
              <Route path="/detect"         element={<RequireAuth><DetectReviewPage /></RequireAuth>} />
              {/* Deep-link / wizard-internal routes — not in primary nav */}
              <Route path="/upload"         element={<RequireAuth><UploadPage /></RequireAuth>} />
              <Route path="/media-library"  element={<RequireAuth><MediaLibraryPage /></RequireAuth>} />
              <Route path="/catalog"        element={<RequireAuth><CatalogBrowserPage /></RequireAuth>} />
              <Route path="/settings"       element={<RequireAuth><SettingsPage /></RequireAuth>} />
              <Route path="/team"           element={<RequireAuth><TeamPage /></RequireAuth>} />
              <Route path="/ad-showcase"    element={<RequireAuth><AdShowcasePage /></RequireAuth>} />
              {/* Sales-only. Server 403s non-Sales-Demos advertisers. */}
              <Route path="/sales-demos"    element={<RequireAuth><SalesDemosPage /></RequireAuth>} />
              {/* Super-admin only. Page + /api/admin/qc-config 403 gate. */}
              <Route path="/admin/settings" element={<RequireAuth><AdminSettingsPage /></RequireAuth>} />
              {/* Super-admin only. Backend GET /api/admin/shopify-requests 403s non-admins. */}
              <Route path="/admin/shopify-requests" element={<RequireAuth><AdminShopifyRequestsPage /></RequireAuth>} />
              <Route path="/admin/shopify-health" element={<RequireAuth><AdminShopifyHealthPage /></RequireAuth>} />
              <Route path="/admin/system-config" element={<RequireAuth><AdminSystemConfigPage /></RequireAuth>} />
              {/* M8 item 7 — nested /v2/settings/audit audit stream
                  viewer, flag-gated OFF. Independent of
                  VITE_SETTINGS_V2_ENABLED so it can ship ahead of the
                  full SettingsV2 shell. Registered BEFORE /v2/settings
                  so the more specific path wins. */}
              {import.meta.env.VITE_AUDIT_V2_ENABLED === 'true' && (
                <Route path="/v2/settings/audit" element={<RequireAuth><AuditV2Page /></RequireAuth>} />
              )}
              {/* M8.1 consolidated Settings surface — gated by
                  VITE_SETTINGS_V2_ENABLED (strict === 'true', default
                  off). Flag off → route is not registered and
                  `/v2/settings` hits the catch-all RootRedirect
                  (authenticated → /home). Flag on → tab strip. Admin,
                  Shopify Health, Shopify Requests, and System Config
                  are super-admin tabs inside the page. The legacy
                  routes above STAY mounted so existing deep links
                  keep working during burn-in. */}
              {import.meta.env.VITE_SETTINGS_V2_ENABLED === 'true' && (
                <Route path="/v2/settings" element={<RequireAuth><SettingsV2Page /></RequireAuth>} />
              )}
              {/* M8.1 Connections merge — registered only when
                  VITE_CONNECTIONS_V2_ENABLED is exactly 'true'.
                  Flag off: this Route is not emitted, so the path
                  falls through to RootRedirect. Flag on: the page
                  renders its sections. /integrations and
                  /admin/shopify-health above stay mounted. */}
              {import.meta.env.VITE_CONNECTIONS_V2_ENABLED === 'true' && (
                <Route path="/v2/connections/*" element={<RequireAuth><ConnectionsV2Page /></RequireAuth>} />
              )}
              {/* M8.1 Agent home — registered only when
                  VITE_HOME_V2_ENABLED is exactly 'true'. Flag off:
                  this Route is not emitted, so /v2/home falls through
                  to RootRedirect (authenticated → /home legacy,
                  unauthenticated → /landing). Flag on: the three-
                  section agent home renders (Briefing + Active Plan
                  Context + Starters). Legacy /home above STAYS mounted
                  so existing bookmarks keep working during burn-in. */}
              {import.meta.env.VITE_HOME_V2_ENABLED === 'true' && (
                <Route path="/v2/home" element={<RequireAuth><HomeV2Page /></RequireAuth>} />
              )}
              {/* M8.1 Catalog V2 — CatalogV2Page mounts the shipped
                  CatalogBrowser at /v2/catalog so M7.3's attention
                  marks + inline nudges (gated on isAgentV2Route inside
                  the sidebar) activate, and adds the §4.5 surface ids
                  on this pathname only. Flag off: Route not emitted.
                  Flag on: /v2/catalog renders the browser. /catalog
                  above stays mounted on CatalogBrowserPage, so it
                  paints no marks (isAgentV2Route('/catalog') is false)
                  and has none of the v2 surface ids. CatalogBrowser
                  itself is unchanged. */}
              {import.meta.env.VITE_CATALOG_V2_ENABLED === 'true' && (
                <Route path="/v2/catalog" element={<RequireAuth><CatalogV2Page /></RequireAuth>} />
              )}
              {/* M8.1 Plans V2 — one route, only when
                  VITE_PLANS_V2_ENABLED is exactly 'true'. Flag off:
                  the Route is not emitted, so /v2/plans/* falls
                  through to RootRedirect (authenticated → /home,
                  unauthenticated → /landing). Flag on: PlansV2Page
                  renders the list, detail, thread, draft, and
                  performance children. Legacy /plans/:planId and
                  /plans/:planId/performance stay mounted. Thread
                  stays a placeholder until ADR-001 PlanMessage. */}
              {import.meta.env.VITE_PLANS_V2_ENABLED === 'true' && (
                <Route path="/v2/plans/*" element={<RequireAuth><PlansV2Page /></RequireAuth>} />
              )}
              {/* M8 item 6 — standalone /v2/runs/activations, flag-
                  gated OFF. Independent of VITE_RUNS_V2_ENABLED so it
                  can ship ahead of the full RunsV2 shell. Registered
                  BEFORE the /v2/runs/* splat so this specific path
                  wins when both flags are on. */}
              {import.meta.env.VITE_ACTIVATIONS_V2_ENABLED === 'true' && (
                <Route path="/v2/runs/activations" element={<RequireAuth><ActivationsV2Page /></RequireAuth>} />
              )}
              {/* M8.2 Runs — one route, only when the flag is exactly
                  'true'. Child paths (list, production, workflow,
                  scene reel, definitions) are registered inside
                  RunsV2Page. Flag off: this Route is not emitted, so
                  the path falls through to RootRedirect. */}
              {import.meta.env.VITE_RUNS_V2_ENABLED === 'true' && (
                <Route path="/v2/runs/*" element={<RequireAuth><RunsV2Page /></RequireAuth>} />
              )}
              {/* M8.2 Decision Inbox — one route, only when
                  VITE_DECISIONS_V2_ENABLED is exactly 'true'. Flag
                  off: this Route is not emitted, so /v2/decisions/*
                  falls through to RootRedirect (authenticated →
                  /home, unauthenticated → /landing). Flag on:
                  DecisionsV2Page renders the list and the detail
                  child. Gate 2 (Budget authorizations) stays
                  read-only until #723 ships. */}
              {import.meta.env.VITE_DECISIONS_V2_ENABLED === 'true' && (
                <Route path="/v2/decisions/*" element={<RequireAuth><DecisionsV2Page /></RequireAuth>} />
              )}
              {/* M8.3 Finance — one splat, only when the flag is
                  exactly 'true'. Child paths (cost-ledger, rate-card,
                  exports, anomalies) are registered inside
                  FinanceV2Page. ?tab= redirects to those paths.
                  Internal cost copy follows the active membership's
                  canViewInternalCost flag, not isSuperAdmin. Flag off:
                  this Route is not emitted. */}
              {import.meta.env.VITE_FINANCE_V2_ENABLED === 'true' && isFinanceV2Enabled() && (
                <Route path="/v2/finance/*" element={<RequireAuth><FinanceV2Page /></RequireAuth>} />
              )}
              {/* M8 item 5 — standalone /v2/brand/rights, flag-gated
                  OFF. The surface is independent of VITE_BRAND_V2_ENABLED
                  so it can ship ahead of the full BrandV2 shell.
                  Registered BEFORE the /v2/brand/* splat so this
                  specific path wins when both flags are on. */}
              {import.meta.env.VITE_RIGHTS_V2_ENABLED === 'true' && (
                <Route path="/v2/brand/rights" element={<RequireAuth><RightsV2Page /></RequireAuth>} />
              )}
              {/* M8.3 Brand consolidation — one splat, only when
                  VITE_BRAND_V2_ENABLED is exactly 'true'. Flag off:
                  the Route is not emitted, so /v2/brand/* falls
                  through to RootRedirect. Flag on: the five-tab shell
                  renders. Legacy /brand stays mounted (default
                  visibilityPreset='full'). */}
              {import.meta.env.VITE_BRAND_V2_ENABLED === 'true' && (
                <Route path="/v2/brand/*" element={<RequireAuth><BrandV2Page /></RequireAuth>} />
              )}
              {/* M8.4 Creative Library — one splat at /v2/catalog/library/*,
                  only when VITE_LIBRARY_V2_ENABLED is exactly 'true'. The
                  list and the detail child are registered inside
                  CreativeLibraryPage. Flag off: this Route is not emitted,
                  so /v2/catalog/library* falls through to RootRedirect
                  (authenticated → /home). The M8.1 /v2/catalog mount and
                  the shipped /catalog browser stay untouched. */}
              {import.meta.env.VITE_LIBRARY_V2_ENABLED === 'true' && (
                <Route path="/v2/catalog/library/*" element={<RequireAuth><CreativeLibraryPage /></RequireAuth>} />
              )}
            </Route>
            {/* Public marketing landing — no app shell, no auth gate. */}
            <Route path="/landing" element={<LandingPage />} />
            {/* Onboarding — authenticated users without an Advertiser
                membership. Reached via apiFetch's 403 NO_ADVERTISER
                redirect; rendered without RequireAuth so the
                logged-in-but-no-workspace state doesn't bounce back
                through the auth gate. */}
            <Route path="/onboarding"           element={<OnboardingPage />} />
            <Route path="/onboarding/workspace" element={<WorkspacePage />} />
            <Route path="/onboarding/brand"     element={<RequireAuth><OnboardingBrandPage /></RequireAuth>} />
            <Route path="/onboarding/connect"   element={<RequireAuth><ConnectPage /></RequireAuth>} />
            <Route path="/onboarding/plan"      element={<RequireAuth><PlanOnboardingPage /></RequireAuth>} />
            {/* Invitation accept — public route. Anonymous invitees
                can preview before signing in; authed users hit POST
                accept and bounce to /home. No RequireAuth wrapper,
                no PipelineShell (no nav/sidebar makes sense for an
                unauth'd visitor). */}
            <Route path="/invite/:token"        element={<InvitePage />} />
            {/* Public ad-showcase snapshot — token IS the auth. No
                RequireAuth, no PipelineShell (same shape as invite). */}
            <Route path="/showcase/:token"      element={<AdShowcasePublicPage />} />
            {/* Root redirects: unauthed → /landing, authed → /brand. */}
            <Route path="/"  element={<RootRedirect />} />
            <Route path="*"  element={<RootRedirect />} />
          </Routes>
      </AgentContextProvider>
    </>
  );
}

// Root entry — sends prospects to the marketing page and authed users
// to their brand workspace. Loading state renders nothing rather than
// flashing a redirect target while AuthProvider resolves the session.
function RootRedirect() {
  const auth = useAuth();
  if (auth.status === 'loading') return null;
  if (auth.status === 'unauthenticated') return <Navigate to="/landing" replace />;
  return <Navigate to="/home" replace />;
}

export function ZaraRoute() {
  const enabled = useZaraEnabled();
  if (!enabled) return <Navigate to="/home" replace />;
  return (
    <Suspense fallback={<Text color="brand.muted" fontSize="sm">Loading Studio Zara</Text>}>
      <ZaraApp />
    </Suspense>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  if (auth.status === 'loading') {
    return (
      <Card>
        <CardBody>
          <Text color="brand.muted" fontSize="sm">Loading session…</Text>
        </CardBody>
      </Card>
    );
  }
  if (auth.status === 'unauthenticated') {
    return (
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={4} py={8}>
            <Box textAlign="center">
              <Heading size="md" color="brand.ink" mb={2}>Sign in to continue</Heading>
              <Text color="brand.muted" fontSize="sm" maxW="420px" mx="auto" mb={6}>
                Reach Social uses Google sign-in. Your account links to the
                Advertiser workspace you've been invited to.
              </Text>
              <Button variant="brand" onClick={auth.signIn}>Sign in with Google</Button>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    );
  }
  return <>{children}</>;
}
